// Kampaňová vrstva (plán kampaní 2026-10-06): deštník nad kategorií akční
// nabídky. Po dobu kampaně se akční nabídka (`/slevoakce/`, SK `/zlavoakcia/`)
// jmenuje podle kampaně („Říjnový výprodej", od 1. 11. „Black Friday") —
// v promo pruhu, menu, na homepage, ve výpisu i na štítcích. URL, admin ani
// admin skupina „Slevoakce" se nemění, přejmenovává jen šablona.
//
// Data jsou manifest shopu `campaigns` (okno v čase obchodu, přepínač, claim
// výprodeje, dlaždice, karta hera), texty `locale/<jazyk>/campaign.js` pod
// klíčem `id` kampaně. Všechny plochy se ptají jen `activeCampaign()`;
// mimo okno nebo s vypnutým přepínačem vrací null a plochy kreslí dnešní stav.
// Okno zhasne samo, konec kampaně nečeká na commit.
import SHOP from '../../config/shop.js';
import { featureOn, projectId, PROJECTS } from './features.js';
import { inShopWindow, parseShopEnd } from './shopTime.js';
import { page } from './pages.js';
import { ga4Event } from './ga4.js';
import L from '../locale/cs/campaign.js';

const CAMPAIGNS = SHOP.campaigns || [];

// Náhled okna na testovacích projektech (dev, testovací SK): `?bs-kampan=2026-11-14`
// (čas obchodu, i s `T10:00`) posune čas kampaní, drží se v sessionStorage do
// `?bs-kampan=off`. Na ostré nic, jiné časové funkce (akce na dopravu, termíny)
// beze změny.
const PREVIEW_KEY = 'bs-kampan';
let previewMs;

function previewNow() {
  if (previewMs !== undefined) return previewMs;
  previewMs = null;
  if (typeof window === 'undefined') return previewMs;
  const p = projectId();
  if (p !== PROJECTS.dev && p !== PROJECTS['sk-dev']) return previewMs;
  let raw = null;
  try {
    raw = new URLSearchParams(window.location.search).get(PREVIEW_KEY);
    if (raw === 'off') window.sessionStorage.removeItem(PREVIEW_KEY);
    else if (raw) window.sessionStorage.setItem(PREVIEW_KEY, raw);
    else raw = window.sessionStorage.getItem(PREVIEW_KEY);
  } catch (e) {
    /* blokované úložiště: náhled platí jen s parametrem v adrese */
  }
  const t = raw && raw !== 'off' ? parseShopEnd(raw) : NaN;
  if (isFinite(t)) previewMs = t;
  return previewMs;
}

/**
 * Kampaň, která právě běží, jinak null.
 *
 * @param {number} [now] epoch ms (výchozí Date.now(), na testovacím projektu náhled `bs-kampan`)
 * @param {number} [project] projekt Shoptetu pro přepínač (výchozí = stránka)
 * @returns {object|null} položka manifestu `campaigns` + `t` (texty kampaně),
 *   `name` (název deštníku), `href` (stránka deštníku), `claim` (číslo „až −X %"
 *   výprodeje, null = bez čísla nebo shop výprodej nemá), `clearance` (popisek
 *   výprodeje s claimem, null = shop výprodej nemá)
 */
export function activeCampaign(now, project) {
  const explicit = arguments.length > 1;
  const at = now != null ? now : previewNow();
  const hit = CAMPAIGNS.find(function (c) {
    const on = explicit ? featureOn(c.feature, project) : featureOn(c.feature);
    return on && inShopWindow(c.from, c.until, at);
  });
  if (!hit) return null;
  const t = L[hit.id];
  if (!t) return null;
  const hasClearance = !!page('clearance');
  return Object.assign({}, hit, {
    t: t,
    name: t.name,
    href: page(hit.page),
    claim: hasClearance ? hit.claimPct : null,
    clearance: hasClearance ? clearanceLabel(hit.claimPct) : null,
  });
}

/**
 * Karty hera s textem kampaně na kartě 0 (`campaigns[].hero`, klíč = video
 * karty v manifestu). Pozice se nemění — poster karty 0 přednačítá hlavička.
 * Text se dosazuje před výměnou videa (`heroWithMedia`), karta se drží videem
 * manifestu.
 * Exportováno kvůli golden testu oken (tools/unit/campaign.test.mjs).
 */
export function heroWithCampaign(cards, campaign) {
  const h = campaign && campaign.hero;
  if (!h || !cards.length || cards[0].video !== h.video) return cards;
  const first = Object.assign({}, cards[0], { badge: h.badge, title: h.title, desc: h.desc, cta: h.cta });
  return [first].concat(cards.slice(1));
}

/**
 * Okna, ve kterých má karta 0 hera vizuál kampaně (`campaigns[].media`):
 * kampaně se zapnutým přepínačem, hranice v epoch ms (čas obchodu, oba konce
 * včetně jako `inShopWindow`). Jediné pravidlo pro kartu (`campaignHeroMedia`)
 * i přednačtený poster v hlavičce HTML kódů: tools/lib/html-includes.mjs
 * `@heroVideoJs` z nich při zápisu zapeče podmínku na čas, takže se vizuál
 * vymění i vrátí sám, bez deploye v den startu (Black Friday, Tomáš 19. 10. 2026).
 *
 * @param {object[]} campaigns manifest `campaigns` (hlavička čte oba shopy)
 * @param {number} [project] projekt Shoptetu (výchozí = stránka)
 * @returns {{from: number, until: number, media: object}[]}
 */
export function campaignHeroWindows(campaigns, project) {
  const explicit = arguments.length > 1;
  return (campaigns || [])
    .filter(function (c) {
      return !!c.media && (explicit ? featureOn(c.feature, project) : featureOn(c.feature));
    })
    .map(function (c) {
      return { from: parseShopEnd(c.from), until: parseShopEnd(c.until), media: c.media };
    })
    .filter(function (w) {
      return !isNaN(w.from) && !isNaN(w.until);
    });
}

/**
 * Vizuál karty 0 hera kampaně, která právě běží (`campaigns[].media`), jinak
 * null. Okno, ne přepínač: mimo okno má karta 0 médium manifestu i se zapnutým
 * přepínačem, takže kampaň jde na ostrou dopředu a vizuál se zapne a vypne sám.
 *
 * @param {object[]} campaigns manifest `campaigns`
 * @param {number} [now] epoch ms (výchozí Date.now(), na testovacím projektu náhled `bs-kampan`)
 * @param {number} [project] projekt Shoptetu (výchozí = stránka)
 */
export function campaignHeroMedia(campaigns, now, project) {
  const at = now != null ? now : previewNow();
  const t = at != null ? at : Date.now();
  const windows = arguments.length > 2 ? campaignHeroWindows(campaigns, project) : campaignHeroWindows(campaigns);
  const hit = windows.find(function (w) {
    return t >= w.from && t <= w.until;
  });
  return hit ? hit.media : null;
}

/** Karty hera s vizuálem kampaně na kartě 0: video, poster, tmavý podklad bez závoje. */
export function heroWithMedia(cards, media) {
  if (!media || !cards.length) return cards;
  const first = Object.assign({}, cards[0], { video: media.video, posterSrc: media.posterSrc, tone: 'dark', overlay: 'none', ink: false });
  return [first].concat(cards.slice(1));
}

/**
 * Tón kampaně na `<html>` (`bs-camp-ink` u Black Friday): pruh, čip v liště,
 * drawer, karta mezi produkty a pás výpisu přepínají barvu jedním selektorem.
 */
export function initCampaignTone() {
  const c = activeCampaign();
  const root = document.documentElement;
  root.classList.toggle('bs-camp-ink', !!c && c.tone === 'ink');
}

/**
 * Název akční nabídky po dobu kampaně („Říjnový výprodej"), jinak null —
 * plocha pak nechá nativní název z adminu („Slevoakce").
 */
export function saleLabel(now) {
  const c = arguments.length ? activeCampaign(now) : activeCampaign();
  return c ? c.name : null;
}

/** „Výprodej skladu až −30 %", bez čísla „Výprodej skladu". */
export function clearanceLabel(claimPct) {
  return L.clearance(claimPct || null);
}

/** Je stránka (cesta) kategorií akční nabídky deštníku? */
export function isSalePath(path) {
  return !!path && path === page('sale');
}

// ---------------------------------------------------------------------------
// Měření ploch kampaně (plán kampaní §Nové funkce 8): GA4 `view_promotion`
// a `select_promotion` přes ga4.js, souhlas řeší Consent Mode v gtag.
// `promotion_id` = id kampaně, `creative_slot` = plocha, `creative_name` = cíl
// odkazu (`sale` deštník, `clearance` výprodej skladu). Bez `items` GA4 event
// do reportu Propagace nezapočte (reference eventů view_promotion) a plocha
// produkt nemá → jeden zástupný item `promo-<id>` s týmiž parametry.
// ---------------------------------------------------------------------------

const SEEN = {};

function promoParams(campaign, slot, target) {
  const p = {
    promotion_id: campaign.id,
    promotion_name: campaign.name,
    creative_name: target,
    creative_slot: slot,
  };
  return Object.assign({}, p, { items: [Object.assign({ item_id: 'promo-' + campaign.id, item_name: campaign.name }, p)] });
}

function sendPromo(event, campaign, slot, target) {
  return ga4Event(event, promoParams(campaign, slot, target));
}

// Proklik = `click` + `auxclick` prostředním tlačítkem (nová záložka), nikdy
// pointerdown. Odkaz, jehož výchozí akci někdo zrušil (drill draweru), se
// nepočítá — zákazník nikam neodešel.
function onLinkClick(root, fn) {
  const handler = function (e) {
    if (e.type === 'auxclick' && e.button !== 1) return;
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a || !root.contains(a) || e.defaultPrevented) return;
    fn(a);
  };
  root.addEventListener('click', handler);
  root.addEventListener('auxclick', handler);
}

/**
 * Plocha kampaně v obsahu stránky (pruh, hero, zóna, bento, pás, dlaždice):
 * zobrazení jednou za stránku a plochu, až je prvek z poloviny ve viewportu,
 * proklik na každý odkaz uvnitř (`opts.clickSel` zúží na vybrané odkazy).
 * Idempotentní na prvku (`data-bs-promo-slot`), takže re-init nic nezdvojí.
 */
export function trackPromo(el, campaign, slot, opts) {
  if (!el || !campaign || el.dataset.bsPromoSlot) return;
  const o = opts || {};
  const target = o.target || 'sale';
  el.dataset.bsPromoSlot = slot;
  onLinkClick(el, function (a) {
    if (o.clickSel && !a.matches(o.clickSel)) return;
    sendPromo('select_promotion', campaign, slot, target);
  });
  if (typeof window.IntersectionObserver !== 'function') return;
  const io = new window.IntersectionObserver(
    function (entries) {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      if (SEEN[slot]) return;
      SEEN[slot] = true;
      sendPromo('view_promotion', campaign, slot, target);
    },
    { threshold: 0.5 }
  );
  io.observe(el);
}

/**
 * Proklik plochy, která nevede odkazem: žlutá dlaždice výprodeje skladu na
 * akční nabídce filtruje výpis na místě (saleBrowse.js), cíl `clearance` zůstává.
 */
export function promoSelected(campaign, slot, target) {
  if (campaign) sendPromo('select_promotion', campaign, slot, target || 'sale');
}

/**
 * Menu (lišta, mega menu, drawer) měří jen proklik: zavřený panel
 * IntersectionObserver od otevřeného neodliší a zobrazení by lhalo. Jeden
 * delegovaný posluchač na dokumentu, cíl podle cesty odkazu.
 */
export function initCampaignTracking() {
  const campaign = activeCampaign();
  if (!campaign || document.documentElement.dataset.bsPromoMenu) return;
  document.documentElement.dataset.bsPromoMenu = '1';
  const clearance = campaign.clearance ? page('clearance') : null;
  onLinkClick(document, function (a) {
    // mega menu visí v liště, proto se ptá první
    const slot = a.closest('.bs-mega')
      ? 'mega_menu'
      : a.closest('.bs-catbar')
        ? 'menu_bar'
        : a.closest('#navigation, .bs-drawer')
          ? 'drawer'
          : null;
    if (!slot) return;
    const path = a.pathname;
    const target = path === campaign.href ? 'sale' : clearance && path === clearance ? 'clearance' : null;
    if (target) sendPromo('select_promotion', campaign, slot, target);
  });
}
